import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  breadcrumbCacheUpdatedEvent,
  getCachedBreadcrumbLabel,
} from "../../helpers/breadcrumbCache";
import { parseBreadcrumbParent } from "../../helpers/breadcrumbNavigation";
import { useAuthInfo } from "../../context/AuthContext";

const Breadcrumbs = () => {
  const location = useLocation();
  const { userInfo } = useAuthInfo();
  const [cacheVersion, setCacheVersion] = useState(0);

  useEffect(() => {
    const handleCacheUpdate = () => {
      setCacheVersion((current) => current + 1);
    };
    window.addEventListener(breadcrumbCacheUpdatedEvent, handleCacheUpdate);
    return () => {
      window.removeEventListener(
        breadcrumbCacheUpdatedEvent,
        handleCacheUpdate
      );
    };
  }, []);

  const crumbs = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    const resolveParentLabel = (type, id, label) => {
      if (label) return label;
      if (id) {
        const cacheType =
          type === "project" ? "project" : type === "sprint" ? "sprint" : null;
        const cached = cacheType
          ? getCachedBreadcrumbLabel(cacheType, id)
          : null;
        if (cached) return cached;
      }
      if (type === "project") return "Project";
      if (type === "sprint") return "Sprint";
      return null;
    };

    let { parentType, parentId, parentLabel } = parseBreadcrumbParent(
      location.search,
      location.state
    );

    parentLabel = resolveParentLabel(parentType, parentId, parentLabel);

    const buildTaskCrumbLink = (taskId) => {
      const params = new URLSearchParams();
      if (parentType) params.set("parent_type", parentType);
      if (parentId) params.set("parent_id", parentId);
      if (parentLabel) params.set("parent_label", parentLabel);
      const search = params.toString();
      return search ? `/task/${taskId}?${search}` : `/task/${taskId}`;
    };
    const pathParts = location.pathname.split("/").filter(Boolean);
    const [root, id] = pathParts;
    const crumbList = [];
    const baseCrumbs = {
      projects: { label: "Projects", link: "/projects" },
      tasks: { label: "Tasks", link: "/tasks" },
      sprints: { label: "Sprints", link: "/sprints" },
      companies: { label: "Companies", link: "/companies" },
      contacts: { label: "Contacts", link: "/contacts" },
      contracts: { label: "Contracts", link: "/contracts" },
      users: { label: "Users", link: "/users" },
      organizations: { label: "Organizations", link: "/organizations" },
      settings: { label: "Settings", link: "/settings" },
      "universal-search": { label: "Search", link: "/universal-search" },
      reports: { label: "Reports", link: "/reports" },
      invoices: { label: "Invoices", link: "/invoices" },
    };

    const addCrumb = (label, link, meta = {}) => {
      crumbList.push({ label, link, ...meta });
    };

    const addBaseCrumb = (key) => {
      const base = baseCrumbs[key];
      if (!base) return false;
      if (key === "tasks") {
        const isMyTasks = searchParams.get("assignedto") === "me";
        addCrumb(
          isMyTasks ? "My Tasks" : base.label,
          isMyTasks ? `/tasks${location.search}` : base.link
        );
        return true;
      }
      addCrumb(base.label, base.link);
      return true;
    };

    const getDetailLabel = (detailPath, fallbackLabel) =>
      getCachedBreadcrumbLabel(detailPath, id) || fallbackLabel;

    const addDetailCrumbs = (listKey, singularLabel, detailPath) => {
      addBaseCrumb(listKey);
      if (id) {
        const detailLabel = getDetailLabel(detailPath, singularLabel);
        addCrumb(detailLabel, `/${detailPath}/${id}`);
      }
      return crumbList;
    };

    const addFormCrumbs = (listKey, singularLabel, detailPath, formPath) => {
      addBaseCrumb(listKey);
      if (id) {
        const detailLabel = getDetailLabel(detailPath, singularLabel);
        addCrumb(detailLabel, `/${detailPath}/${id}`);
        const editLabel =
          detailLabel !== singularLabel
            ? `Editing - ${detailLabel}`
            : `Editing - ${singularLabel}`;
        addCrumb(editLabel, `/${formPath}/${id}`);
      } else {
        addCrumb(`New ${singularLabel}`, `/${formPath}`);
      }
      return crumbList;
    };

    addCrumb("Home", "/home");

    if (!root || root === "home") {
      return crumbList;
    }

    const rootHandlers = {
      project: () => addDetailCrumbs("projects", "Project", "project"),
      "project-form": () =>
        addFormCrumbs("projects", "Project", "project", "project-form"),
      tasks: () => {
        addBaseCrumb("tasks");
        return crumbList;
      },
      task: () => {
        if (parentType === "mytasks") {
          addCrumb("My Tasks", "/tasks?assignedto=me");

          if (id) {
            const detailLabel = getDetailLabel("task", "Task");
            addCrumb(detailLabel, buildTaskCrumbLink(id));
          }

          return crumbList;
        }
        if (parentType === "project") {
          addCrumb("Projects", "/projects");
          addCrumb(parentLabel, `/project/${parentId}`);

          if (id) {
            const detailLabel = getDetailLabel("task", "Task");
            addCrumb(detailLabel, buildTaskCrumbLink(id));
          }

          return crumbList;
        } else if (parentType === "sprint") {
          addCrumb("Sprints", "/sprints");
          addCrumb(parentLabel, `/sprint/${parentId}`);

          if (id) {
            const detailLabel = getDetailLabel("task", "Task");
            addCrumb(detailLabel, buildTaskCrumbLink(id));
          }

          return crumbList;
        }

        return addDetailCrumbs("tasks", "Task", "task");
      },
      "task-form": () => {
        if (parentType === "project") {
          addCrumb("Projects", "/projects");
          addCrumb(parentLabel, `/project/${parentId}`);
          addCrumb("New Task", location.pathname + location.search);

          return crumbList;
        }

        if (parentType === "sprint") {
          addCrumb("Sprints", "/sprints");
          addCrumb(parentLabel, `/sprint/${parentId}`);
          addCrumb("New Task", location.pathname + location.search);

          return crumbList;
        }

        return addFormCrumbs("tasks", "Task", "task", "task-form");
      },
      sprint: () => addDetailCrumbs("sprints", "Sprint", "sprint"),
      "sprint-form": () =>
        addFormCrumbs("sprints", "Sprint", "sprint", "sprint-form"),
      company: () => addDetailCrumbs("companies", "Company", "company"),
      "company-form": () =>
        addFormCrumbs("companies", "Company", "company", "company-form"),
      contract: () => addDetailCrumbs("contracts", "Contract", "contract"),
      "contact-add": () =>
        addFormCrumbs("contacts", "Contact", "contact-add", "contact-add"),
      user: () => {
        const isOwnProfile =
          userInfo?.user_id != null && String(userInfo.user_id) === String(id);
        if (isOwnProfile) {
          addCrumb("My Profile", `/user/${id}`);
          return crumbList;
        }
        return addDetailCrumbs("users", "User", "user");
      },
      "user-add": () => addFormCrumbs("users", "User", "user", "user-add"),
      organization: () =>
        addDetailCrumbs("organizations", "Organization", "organization"),
      "organization-form": () =>
        addFormCrumbs(
          "organizations",
          "Organization",
          "organization",
          "organization-form"
        ),
    };

    if (rootHandlers[root]) {
      return rootHandlers[root]();
    }

    if (addBaseCrumb(root)) {
      return crumbList;
    }

    return crumbList;
  }, [
    location.pathname,
    location.search,
    location.state,
    cacheVersion,
    userInfo?.user_id,
  ]);

  if (crumbs.length <= 1) {
    return null;
  }

  return (
    <div className="breadcrumbs-wrapper">
      <div className="breadcrumbs">
        {crumbs.map((crumb, index) => (
          <div className="breadcrumb-item" key={`${crumb.link}-${index}`}>
            <Link to={crumb.link}>{crumb.label}</Link>
            {index < crumbs.length - 1 && (
              <span className="breadcrumb-separator">/</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Breadcrumbs;
